import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { Modal } from '../components/Modal';
import { Tabs } from '../components/Tabs';
import { Icon } from '../components/Icons';
import { btnGhost, btnPrimary, tableStyle, tdStyle, thStyle } from '../components/buttons';
import { adminApi } from '../api/admin';
import { fmtDate, fmtUSD } from '../lib/format';
import type { PayoutWithCreatorDto } from '../api/types';
import type { ApiError } from '../api/client';

type TabId = 'scheduled' | 'paid' | 'all';

export function PayoutsScreen() {
  const [tab, setTab] = useState<TabId>('scheduled');
  const [markingPaid, setMarkingPaid] = useState<PayoutWithCreatorDto | null>(null);
  const [markNotes, setMarkNotes] = useState('');
  const [markReceiptUrl, setMarkReceiptUrl] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const payouts = useQuery({
    queryKey: ['admin', 'payouts', tab],
    queryFn: () => adminApi.listPayouts(tab === 'all' ? undefined : tab),
  });

  const runCron = useMutation({
    mutationFn: () => adminApi.runPayouts(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin'] }),
    onError: (err: ApiError) => setActionError(err?.message ?? 'Failed'),
  });

  const markPaid = useMutation({
    mutationFn: ({ id, body }: { id: number; body: { notes?: string; receiptUrl?: string } }) =>
      adminApi.markPayoutPaid(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      setMarkingPaid(null);
      setMarkNotes('');
      setMarkReceiptUrl('');
    },
    onError: (err: ApiError) => setActionError(err?.message ?? 'Mark paid failed'),
  });

  const rows = payouts.data ?? [];

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1280 }}>
      <Card padding={0}>
        <div style={{ padding: '6px 20px 0' }}>
          <Tabs
            value={tab}
            onChange={(id) => setTab(id as TabId)}
            tabs={[
              { id: 'scheduled', label: 'Scheduled', count: rows.filter((p) => p.status === 'scheduled').length },
              { id: 'paid', label: 'Paid' },
              { id: 'all', label: 'All' },
            ]}
            right={
              <button style={btnGhost} onClick={() => runCron.mutate()} disabled={runCron.isPending}>
                <Icon.Calendar />
                {runCron.isPending ? 'Running…' : 'Run monthly close'}
              </button>
            }
          />
        </div>

        {actionError && (
          <div
            style={{
              margin: 16,
              fontSize: 12.5,
              color: 'var(--bad)',
              background: 'color-mix(in oklab, var(--bad) 10%, var(--bg-elev))',
              border: '1px solid color-mix(in oklab, var(--bad) 25%, transparent)',
              padding: '8px 12px',
              borderRadius: 'var(--r-sm)',
            }}
          >
            {actionError}
          </div>
        )}

        {tab === 'scheduled' && (
          <div style={{ padding: '12px 20px', background: 'var(--bg-sunken)', borderBottom: '1px solid var(--line)', fontSize: 12.5, color: 'var(--ink-2)' }}>
            Send the money out-of-band (bank transfer / PayPal) then mark each row paid. The cron runs automatically on the 1st of every month.
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Payout ID</th>
                <th style={thStyle}>Creator</th>
                <th style={thStyle}>Period</th>
                <th style={thStyle}>Method</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--line)', background: p.status === 'scheduled' ? 'var(--brand-soft)' : undefined }}>
                  <td style={tdStyle}><span className="mono" style={{ fontWeight: 600 }}>{p.publicId}</span></td>
                  <td style={tdStyle}>
                    {p.creator?.id ? (
                      <Link to={`/creators/${p.creator.id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{p.creator.name ?? '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>@{p.creator.handle ?? '—'}</div>
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{fmtDate(p.periodMonth)}</td>
                  <td style={{ ...tdStyle, color: 'var(--ink-2)' }}>{p.methodLabel}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }} className="num">{fmtUSD(p.amountCents)}</td>
                  <td style={tdStyle}><StatusPill status={p.status} /></td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {p.status === 'scheduled' && (
                      <button
                        style={btnPrimary}
                        onClick={() => {
                          setMarkingPaid(p);
                          setMarkNotes('');
                          setMarkReceiptUrl('');
                        }}
                      >
                        <Icon.Check />
                        Mark paid
                      </button>
                    )}
                    {p.status === 'paid' && p.receiptUrl && (
                      <a href={p.receiptUrl} target="_blank" rel="noreferrer" style={{ ...btnGhost, textDecoration: 'none' }}>
                        <Icon.Download />
                        Receipt
                      </a>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} style={{ ...tdStyle, color: 'var(--ink-3)', textAlign: 'center', padding: 60 }}>
                  {payouts.isLoading ? 'Loading…' : tab === 'scheduled' ? 'Queue is empty 🎉' : 'No payouts here yet.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={!!markingPaid}
        onClose={() => setMarkingPaid(null)}
        title={markingPaid ? `Mark ${markingPaid.publicId} paid` : ''}
        description={markingPaid ? `Confirm you've sent ${fmtUSD(markingPaid.amountCents)} to ${markingPaid.creator.name ?? markingPaid.creator.handle}.` : ''}
        footer={
          <>
            <button style={btnGhost} onClick={() => setMarkingPaid(null)}>Cancel</button>
            <button
              style={btnPrimary}
              disabled={markPaid.isPending}
              onClick={() => markingPaid && markPaid.mutate({ id: Number(markingPaid.id), body: {
                notes: markNotes || undefined,
                receiptUrl: markReceiptUrl || undefined,
              } })}
            >
              {markPaid.isPending ? 'Marking…' : 'Mark paid'}
            </button>
          </>
        }
      >
        <ModalField label="Receipt URL (optional)">
          <input
            value={markReceiptUrl}
            onChange={(e) => setMarkReceiptUrl(e.target.value)}
            placeholder="https://your-bank-or-stripe-receipt-link"
            style={inputStyle}
          />
        </ModalField>
        <ModalField label="Notes (optional)">
          <textarea
            value={markNotes}
            onChange={(e) => setMarkNotes(e.target.value)}
            rows={3}
            placeholder="Internal note — wire reference, batch id…"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'var(--font-body)' }}
          />
        </ModalField>
      </Modal>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  if (status === 'paid') return <Pill tone="good" size="sm">Paid</Pill>;
  if (status === 'scheduled') return <Pill tone="warn" size="sm">Auto-scheduled</Pill>;
  if (status === 'processing') return <Pill tone="warn" size="sm">Processing</Pill>;
  if (status === 'failed') return <Pill tone="bad" size="sm">Failed</Pill>;
  if (status === 'skipped') return <Pill tone="neutral" size="sm">Skipped</Pill>;
  return <Pill tone="neutral" size="sm">{status}</Pill>;
}

const inputStyle = {
  background: 'var(--bg-elev)',
  border: '1px solid var(--line-strong)',
  borderRadius: 'var(--r-md)',
  padding: '10px 12px',
  fontSize: 14,
  color: 'var(--ink)',
  outline: 'none',
  width: '100%',
} as const;

function ModalField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
